/**
 * onboardingApi.ts - Client wrapper pour les endpoints /api/onboarding/*
 */
import { apiFetch } from './apiClient';
import { DEMO_MODE } from './liveMode';
import { demoOnboardingStatus } from '../demo/demoData';

export interface OnboardingStatus {
  connected:        boolean;
  needsOnboarding:  boolean;
  tenantId?:        string;
  organizationName?: string;
  consentedAt?:     string;
  consentedBy?:     { userId: string; email: string; name: string } | string;
  lastTestAt?:      string | null;
  lastTestSuccess?: boolean | null;
  // v6 : domaines verifies pour l affichage UI
  onmsDomain?:      string;     // ex: contoso.onmicrosoft.com
  defaultDomain?:   string;     // ex: contoso.com (custom domain par defaut si configure)
  verifiedDomains?: Array<{ name: string; isDefault?: boolean }>;
  // v1.2 - personnalisation persistee + stats tenant
  companyName?:     string | null;
  techContact?:     string | null;
  entraIdSku?:      'Free' | 'P1' | 'P2' | null;
  userCount?:       number | null;
  // v1.3 - langue par defaut des emails de notification (fr-BE | nl-BE | de-BE | en)
  notificationLanguage?: 'fr-BE' | 'nl-BE' | 'de-BE' | 'en' | null;
  // Partenaire rattaché au client (signup avec partenaire). Vide = auto-signup sans partenaire.
  // partnerId = id du doc partenaire (source de vérité côté signup/admin) ;
  // partnerSlug = slug résolu par le backend (pour matcher les cartes du portail).
  partnerId?:   string | null;
  partnerSlug?: string | null;
  partnerName?: string | null;
  // Identité entreprise (page de garde + chapitre du rapport CyFun)
  companyLegalName?:  string | null;
  companyBce?:        string | null;
  companyAddress?:    string | null;
  companyPostalCode?: string | null;
  companyCity?:       string | null;
  companyCountry?:    string | null;
  // Logo de l'organisation (data URL) — utilisé dans les rapports/documents.
  companyLogo?:       string | null;
}

export interface UpdateCustomizationPayload {
  companyName?: string;
  techContact?: string;
  companyLegalName?:  string;
  companyBce?:        string;
  companyAddress?:    string;
  companyPostalCode?: string;
  companyCity?:       string;
  companyCountry?:    string;
  companyLogo?:       string;
}

export interface UpdateCustomizationResponse {
  success:     boolean;
  companyName: string;
  techContact: string;
  companyLegalName?:  string;
  companyBce?:        string;
  companyAddress?:    string;
  companyPostalCode?: string;
  companyCity?:       string;
  companyCountry?:    string;
  updatedAt:   string;
  message:     string;
}

export interface StartConsentResponse {
  consentUrl:  string;
  redirectUri: string;
  state:       string;
  message:     string;
}

export interface TestConnectionResponse {
  success:           boolean;
  tenantId?:         string;
  organizationName?: string;
  verifiedDomains?:  Array<{ name: string; isDefault: boolean }>;
  message?:          string;
  error?:            string;
  hint?:             string;
}

export type PermissionTestStatus = 'ok' | 'forbidden' | 'notFound' | 'badReq' | 'license' | 'error';

export interface PermissionTestResult {
  name:     string;
  category: 'core' | 'identity' | 'security' | 'purview' | 'intune' | 'exo' | 'defender' | string;
  status:   PermissionTestStatus;
  httpCode: number;
  error?:   string;
  hint?:    string | null;
}

export interface TestPermissionsResponse {
  success:  boolean;
  tenantId?:string;
  runAt?:   string;
  results:  PermissionTestResult[];
  summary?: {
    total: number;
    ok:    number;
    ko:    number;
    byCategory: Array<{ category: string; total: number; ok: number; ko: number }>;
  };
  error?:   string;
  hint?:    string;
}

// Note : la gestion 401 → session-expired event est dans apiFetch (apiClient.ts)

// Base API (dupliquee ici car apiFetch impose un parsing JSON, inadapte au
// telechargement du script .ps1 servi en text/plain avec Content-Disposition).
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * Telecharge le script PowerShell pre-rempli (EXO + Purview) genere par
 * /api/onboarding/setup-script et declenche l enregistrement du fichier.
 * Re-active 2026.07 : les checks EXO/EM auto-evaluent via Exchange Online REST,
 * ce qui necessite a nouveau Exchange.ManageAsApp + les role groups EXO/Purview.
 */
async function downloadSetupScript(): Promise<void> {
  const res = await fetch(`${API_BASE}/onboarding/setup-script`, { credentials: 'include' });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('nis365:session-expired', { detail: { reason: 'setup-script' } }));
    throw new Error('Session expirée — reconnexion requise');
  }
  if (!res.ok) throw new Error(`Téléchargement du script échoué (HTTP ${res.status})`);
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = 'nis365-setup-exo-purview.ps1';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const onboardingApi = {
  downloadSetupScript,
  status:          ()                  => DEMO_MODE ? Promise.resolve(demoOnboardingStatus()) : apiFetch<OnboardingStatus>('/onboarding/status'),
  startConsent:    (tenantId?: string) => apiFetch<StartConsentResponse>('/onboarding/start-consent', {
    method: 'POST',
    body:   JSON.stringify({ tenantId: tenantId ?? '' }),
  }),
  testConnection:  ()                  => apiFetch<TestConnectionResponse>('/onboarding/test-connection', { method: 'POST' }),
  testPermissions: ()                  => apiFetch<TestPermissionsResponse>('/onboarding/test-permissions', { method: 'POST' }),
  /** Persiste les champs companyName + techContact dans le doc Cosmos clients. */
  updateCustomization: (p: UpdateCustomizationPayload) =>
    apiFetch<UpdateCustomizationResponse>('/clients/customization', {
      method: 'POST',
      body:   JSON.stringify(p),
    }),
};
